import { useRef, useState } from "react";
import { useVideos } from "@/hooks/content";
import type { VideoItem } from "@/mocks/videos";
import {
  PageHeader,
  ConfirmDialog,
  useToast,
  newId,
} from "./components/admin-ui";
import {
  uploadFileWithProgress,
  readVideoDuration,
  formatDuration,
  uniquePath,
} from "@/lib/supabase";

const categories = [
  "News",
  "Interviews",
  "Reports",
  "Talk Shows",
  "Entertainment",
  "Culture",
  "Religion",
  "Children",
  "Sports",
  "Documentaries",
  "L+TV Direct",
  "Behind the Scenes",
  "Short Videos",
];

interface UploadForm {
  title: string;
  description: string;
  category: string;
  tags: string;
  thumbnail: File | null;
  video: File | null;
}

const emptyForm: UploadForm = {
  title: "",
  description: "",
  category: "",
  tags: "",
  thumbnail: null,
  video: null,
};

function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "")
    .slice(0, 80);
}

export default function VideosAdminPage() {
  const { items, loading, error, add, remove } = useVideos();
  const { show } = useToast();

  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState<UploadForm>(emptyForm);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [confirmId, setConfirmId] = useState<string | null>(null);

  const thumbInputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);

  function resetForm() {
    setForm(emptyForm);
    setProgress(0);
    setUploading(false);
  }

  async function handleSave() {
    if (!form.title.trim()) {
      show("Please enter a title.", "error");
      return;
    }
    if (!form.video) {
      show("Please choose a video file to upload.", "error");
      return;
    }

    setUploading(true);
    setProgress(0);

    try {
      const videoExt = form.video.name.includes(".")
        ? form.video.name.slice(form.video.name.lastIndexOf("."))
        : ".mp4";
      const videoPath = uniquePath("uploads", `${slugify(form.title) || "video"}${videoExt}`);

      const videoUrl = await uploadFileWithProgress({
        bucket: "videos",
        path: videoPath,
        file: form.video,
        onProgress: (p) => setProgress(p),
      });

      let thumbnail = "";
      if (form.thumbnail) {
        const thumbExt = form.thumbnail.name.includes(".")
          ? form.thumbnail.name.slice(form.thumbnail.name.lastIndexOf("."))
          : ".jpg";
        thumbnail = await uploadFileWithProgress({
          bucket: "videos",
          path: uniquePath("thumbs", `thumb${thumbExt}`),
          file: form.thumbnail,
        });
      }

      const seconds = await readVideoDuration(form.video);
      const duration = formatDuration(seconds);

      const record: VideoItem = {
        id: newId(),
        slug: slugify(form.title),
        title: form.title.trim(),
        description: form.description.trim(),
        thumbnail,
        videoUrl,
        category: form.category || "News",
        tags: form.tags
          .split(",")
          .map((t) => t.trim())
          .filter(Boolean),
        publishedAt: new Date().toISOString().slice(0, 10),
        duration,
        views: "0",
        featured: false,
      };

      await add(record);
      show("Video uploaded successfully.");
      setModalOpen(false);
      resetForm();
    } catch (e) {
      show(e instanceof Error ? e.message : "Upload failed. Please try again.", "error");
    } finally {
      setUploading(false);
    }
  }

  const confirmItem = items.find((v) => v.id === confirmId) ?? null;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Videos"
        subtitle="Upload and manage on-demand videos shown to viewers on the website."
        actions={
          <button
            type="button"
            onClick={() => {
              resetForm();
              setModalOpen(true);
            }}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-md hover:bg-primary-700 whitespace-nowrap"
          >
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-upload-cloud-line text-sm" />
            </span>
            Upload Video
          </button>
        }
      />

      {loading ? (
        <div className="bg-white rounded-lg border border-background-200 p-10 text-center text-sm text-foreground-500">
          Loading videos…
        </div>
      ) : error ? (
        <div className="bg-accent-50 border border-accent-200 rounded-lg p-6 text-sm text-accent-700">
          <p className="font-semibold">Couldn't load videos.</p>
          <p className="mt-1">{error}</p>
        </div>
      ) : items.length === 0 ? (
        <div className="bg-white rounded-lg border border-background-200 p-12 text-center">
          <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
            <i className="ri-movie-line text-xl" />
          </div>
          <p className="mt-3 text-sm text-foreground-600">No videos yet. Upload your first video.</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-background-200 divide-y divide-background-50">
          {items.map((v) => (
            <div key={v.id} className="flex items-center gap-4 px-4 py-3">
              <div className="relative w-24 h-14 rounded-md overflow-hidden bg-background-100 shrink-0">
                {v.thumbnail ? (
                  <img src={v.thumbnail} alt={v.title} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-foreground-300">
                    <i className="ri-movie-line text-lg" />
                  </div>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-foreground-950 truncate">{v.title}</p>
                <p className="text-xs text-foreground-500 mt-0.5">
                  {v.category}
                  {v.duration ? ` · ${v.duration}` : ""} · {v.publishedAt}
                </p>
                {v.videoUrl && (
                  <p className="text-[11px] text-green-600 mt-0.5 truncate">Stored in cloud storage</p>
                )}
              </div>
              <button
                type="button"
                onClick={() => setConfirmId(v.id)}
                className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-accent-50 text-accent-600 shrink-0"
                aria-label="Delete video"
              >
                <i className="ri-delete-bin-line text-base" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Upload modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center p-4 overflow-y-auto">
          <div
            className="fixed inset-0 bg-foreground-900/50"
            onClick={() => !uploading && setModalOpen(false)}
            aria-hidden="true"
          />
          <div className="relative bg-white rounded-lg w-full max-w-2xl my-8 flex flex-col max-h-[90vh]">
            <header className="flex items-start justify-between gap-3 px-5 py-4 border-b border-background-100">
              <div>
                <h2 className="text-base font-semibold text-foreground-950">Upload Video</h2>
                <p className="text-xs text-foreground-500 mt-0.5">
                  Files are stored in your Supabase storage and shown on the public website.
                </p>
              </div>
              <button
                type="button"
                onClick={() => !uploading && setModalOpen(false)}
                className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-background-100 text-foreground-500"
                aria-label="Close"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </header>

            <div className="overflow-y-auto px-5 py-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground-800 mb-1.5">
                  Title <span className="text-accent-500">*</span>
                </label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
                  placeholder="Video title"
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground-800 mb-1.5">Description</label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                  rows={3}
                  placeholder="Short description shown to viewers"
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground-800 mb-1.5">Category</label>
                  <select
                    value={form.category}
                    onChange={(e) => setForm((p) => ({ ...p, category: e.target.value }))}
                    className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
                  >
                    <option value="">Select…</option>
                    {categories.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground-800 mb-1.5">Tags</label>
                  <input
                    type="text"
                    value={form.tags}
                    onChange={(e) => setForm((p) => ({ ...p, tags: e.target.value }))}
                    placeholder="Comma-separated"
                    className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
                  />
                </div>
              </div>

              {/* Thumbnail */}
              <div>
                <label className="block text-sm font-medium text-foreground-800 mb-1.5">Thumbnail (image, optional)</label>
                <input
                  ref={thumbInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => setForm((p) => ({ ...p, thumbnail: e.target.files?.[0] ?? null }))}
                />
                <button
                  type="button"
                  onClick={() => thumbInputRef.current?.click()}
                  className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-foreground-700 border border-background-200 rounded-md hover:bg-background-50 whitespace-nowrap"
                >
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-image-add-line text-sm" />
                  </span>
                  {form.thumbnail ? form.thumbnail.name : "Choose thumbnail image"}
                </button>
              </div>

              {/* Video file */}
              <div>
                <label className="block text-sm font-medium text-foreground-800 mb-1.5">
                  Video file (MP4) <span className="text-accent-500">*</span>
                </label>
                <input
                  ref={videoInputRef}
                  type="file"
                  accept="video/mp4,video/*"
                  className="hidden"
                  onChange={(e) => setForm((p) => ({ ...p, video: e.target.files?.[0] ?? null }))}
                />
                <button
                  type="button"
                  onClick={() => videoInputRef.current?.click()}
                  className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-foreground-700 border border-background-200 rounded-md hover:bg-background-50 whitespace-nowrap"
                >
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-video-add-line text-sm" />
                  </span>
                  {form.video ? form.video.name : "Choose video file"}
                </button>
              </div>

              {uploading && (
                <div>
                  <div className="flex items-center justify-between text-xs text-foreground-600 mb-1.5">
                    <span>Uploading…</span>
                    <span className="font-semibold">{progress}%</span>
                  </div>
                  <div className="h-2 bg-background-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-primary-500 rounded-full transition-all duration-200"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                </div>
              )}
            </div>

            <footer className="flex flex-wrap items-center justify-end gap-2 px-5 py-4 border-t border-background-100">
              <button
                type="button"
                onClick={() => setModalOpen(false)}
                disabled={uploading}
                className="px-4 py-2 text-sm font-medium text-foreground-700 border border-background-200 rounded-md hover:bg-background-50 disabled:opacity-50 whitespace-nowrap"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={uploading}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50 whitespace-nowrap"
              >
                {uploading ? (
                  <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                ) : (
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-upload-cloud-line text-sm" />
                  </span>
                )}
                {uploading ? "Uploading…" : "Upload Video"}
              </button>
            </footer>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={confirmId !== null}
        title="Delete video?"
        message={
          confirmItem
            ? `Are you sure you want to delete "${confirmItem.title}"? It will be removed from the public website.`
            : ""
        }
        onConfirm={() => {
          if (confirmId) {
            remove(confirmId);
            show("Video deleted.");
          }
          setConfirmId(null);
        }}
        onCancel={() => setConfirmId(null)}
      />
    </div>
  );
}